import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Wordmark } from '@tmr/design-system';

/** The sidebar element's id, which the toggle points `aria-controls` at. */
export const SIDEBAR_ID = 'portal-sidebar';

/** One entry of the sidebar. */
export interface SidebarItem {
  /** Stable React key. */
  key: string;
  /** What the operator reads, in PT-BR. */
  label: string;
  /** The glyph, already rendered by the app — the package ships no icons. */
  icon: ReactNode;
  /** Route the item opens. Absent while the area has no screen, which renders the item as pending. */
  path?: string;
  /**
   * Match the route exactly instead of by prefix. A root item (`/`) needs it, or it would be marked on every
   * page; an item with sub-routes usually wants the prefix, so it stays marked inside them.
   */
  end?: boolean;
  /** Why a pending item is listed but out of reach, in the operator's words. */
  pendingReason?: string;
}

/** A labelled run of sidebar items. */
export interface SidebarGroup {
  /** The group's heading, in PT-BR. */
  label: string;
  items: readonly SidebarItem[];
}

/** Props for {@link PortalSidebar}. */
export interface PortalSidebarProps {
  /** The line under the wordmark that names the portal, e.g. "Portal administrativo". */
  caption: string;
  groups: readonly SidebarGroup[];
  /** The bottom of the rail — normally a `SidebarProfile`. */
  footer?: ReactNode;
  /** Whether the mobile drawer is open. The desktop rail ignores it: it opens on hover and keyboard focus. */
  open: boolean;
  /** Closes the drawer; called when the scrim behind it is tapped. */
  onClose: () => void;
  /** Accessible name of the navigation landmark. */
  navLabel?: string;
  /** The tag a pending item carries. */
  pendingTag?: string;
}

/**
 * The portals' sidebar: a 64px rail that expands to 250px on hover or keyboard focus, and an off-canvas
 * drawer at 900px and below.
 *
 * It is the Portal do Administrador's sidebar, extracted so every portal renders the same one. Colors come
 * from design-system tokens through `--pk-*` variables the package reads but never declares, so each portal
 * paints its own rail in its own style layer (see `sidebar.css`).
 *
 * The scrim and the `<aside>` are returned as siblings, so both sit directly in the portal's flex shell.
 */
export function PortalSidebar({
  caption,
  groups,
  footer,
  open,
  onClose,
  navLabel = 'Menu principal',
  pendingTag = 'Em breve',
}: PortalSidebarProps) {
  return (
    <>
      <div className="pk-scrim" data-open={open} aria-hidden="true" onClick={onClose} />
      <aside id={SIDEBAR_ID} className="pk-sidebar" data-open={open}>
        <div className="pk-sidebar-inner">
          <div className="pk-brand">
            <span className="pk-brand-mark">
              <Wordmark size={26} color="var(--fg-inverse)" />
            </span>
            <div className="pk-collapsible pk-caption">{caption}</div>
          </div>

          <nav className="pk-nav" aria-label={navLabel}>
            {groups.map((group) => (
              <div key={group.label} className="pk-group">
                <div className="pk-collapsible pk-group-label">{group.label}</div>
                <div className="pk-items">
                  {group.items.map((item) =>
                    item.path ? (
                      <NavLink key={item.key} to={item.path} end={item.end} className="pk-item">
                        <span className="pk-item-icon">{item.icon}</span>
                        <span className="pk-collapsible pk-item-label">{item.label}</span>
                      </NavLink>
                    ) : (
                      // Present and honestly out of reach: a disabled link role on a real element rather than
                      // a link that goes nowhere, with the reason on the item itself instead of on a screen.
                      <span
                        key={item.key}
                        className="pk-item"
                        role="link"
                        aria-disabled="true"
                        aria-label={item.pendingReason ? `${item.label} — ${item.pendingReason}` : item.label}
                        title={item.pendingReason}
                      >
                        <span className="pk-item-icon">{item.icon}</span>
                        <span className="pk-collapsible pk-item-label">{item.label}</span>
                        <span className="pk-collapsible pk-item-tag">{pendingTag}</span>
                      </span>
                    ),
                  )}
                </div>
              </div>
            ))}
          </nav>

          {footer && <div className="pk-footer">{footer}</div>}
        </div>
      </aside>
    </>
  );
}
